import { useState } from 'react'
import type { ReactNode } from 'react'
import { Navigate, useParams } from 'react-router'
import { EmptyState, FlowLayout, Icon, RadioCards, RangeCalendar, Skeleton } from '@/ds'
import type { DateRange } from '@/ds'
import { formatMoney, formatMoneyShort, time, weekdayDayMonth } from '@/lib/format'
import { useAppNavigate, withQuery } from '@/lib/query-params'
import { asyncValue, useAsync } from '@/lib/useAsync'
import { reservaService } from '@/services/reserva'
import type { ChangeInput, ChangeType, Stay } from '@/services/reserva'
import { useApiLang } from '../Checkin/useSave'
import { SectionError } from '../Estancia/sections/SectionError'
import { BOOKING_ROUTES, dayOf, noon, signed, TYPE_BY_SLUG, useBookingStay, useLang, writeDraft, useChangeExit } from './shared'

const TOTAL_STEPS = 4

/** Suma días a `yyyy-MM-dd`. */
function addDays(day: string, n: number): string {
  const d = new Date(noon(day))
  d.setDate(d.getDate() + n)
  return dayOf(d.toISOString())
}
const nightsBetween = (from: string, to: string) => Math.round((new Date(noon(to)).getTime() - new Date(noon(from)).getTime()) / 86400000)
const monthOf = (day: string) => day.slice(0, 7)
const lastDayOf = (month: string) => {
  const [y, m] = month.split('-').map(Number) as [number, number]
  return `${month}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`
}

interface StepProps {
  stay: Stay
  type: ChangeType
  onNext: (input: ChangeInput) => void
}

/** Pantalla del paso 1 (Reserva.dc.html screen=pick): Atrás a la reserva, "Paso 1 de 4" y acción fija abajo. */
function Step({ title, intro, children, primaryLabel, onPrimary, disabled, hint }: { title: ReactNode; intro?: ReactNode; children: ReactNode; primaryLabel: ReactNode; onPrimary: () => void; disabled: boolean; hint?: ReactNode }) {
  const back = useChangeExit()
  return (
    <FlowLayout
      progress={{ step: 1, total: TOTAL_STEPS, backLabel: back.backLabel, onBack: () => back.exit(), saved: false }}
      title={title}
      intro={intro}
      primary={{ label: primaryLabel, onClick: onPrimary, disabled }}
    >
      {children}
      {/* Bajo el contenido, como el bundle (§5b); explica también por qué el botón está desactivado. */}
      <div aria-live="polite">{hint}</div>
    </FlowLayout>
  )
}

/** Contador con −/+ de 48 px y el valor anunciado (noches, adultos, niños). */
function Stepper({ label, value, valueText, onChange, min, max, decLabel, incLabel }: { label: ReactNode; value: number; valueText: string; onChange: (n: number) => void; min: number; max: number; decLabel: string; incLabel: string }) {
  return (
    <div className="reserva__stepper" role="group" aria-label={typeof label === 'string' ? label : undefined}>
      <span className="reserva__stepper-label">{label}</span>
      <div className="reserva__stepper-controls">
        <button type="button" className="reserva__stepper-button" aria-label={decLabel} disabled={value <= min} onClick={() => onChange(value - 1)}>
          <Icon name="minus" size={22} />
        </button>
        <output className="reserva__stepper-value tnum" aria-live="polite">
          {valueText}
        </output>
        <button type="button" className="reserva__stepper-button" aria-label={incLabel} disabled={value >= max} onClick={() => onChange(value + 1)}>
          <Icon name="plus" size={22} />
        </button>
      </div>
    </div>
  )
}

function DatesStep({ stay, type, onNext }: StepProps) {
  const { t, lang } = useLang()
  const current: DateRange = { from: dayOf(stay.dates.checkIn), to: dayOf(stay.dates.checkOut) }
  const today = dayOf(new Date().toISOString())
  const [range, setRange] = useState<DateRange>(current)
  const [month, setMonth] = useState(() => monthOf((current.from ?? today) < today ? today : (current.from ?? today)))
  const [availability] = useAsync(`disponibilidad:${stay.guestCode}:${month}`, (signal) => reservaService.getAvailability(stay, `${month}-01`, lastDayOf(month), signal))
  const busy = asyncValue(availability)?.busyDates ?? []
  const nights = range.from && range.to ? nightsBetween(range.from, range.to) : null
  const unchanged = range.from === current.from && range.to === current.to
  const choosingCheckout = Boolean(range.from && !range.to)

  let hint: string
  if (nights === null) hint = t('v2.reserva.elegir.fechas.eligeSalida')
  else if (unchanged) hint = t('v2.reserva.elegir.fechas.iguales')
  else hint = `${nights === stay.dates.nights ? t('v2.reserva.elegir.fechas.mismas', { count: nights }) : t('v2.reserva.elegir.fechas.noches', { count: nights })} ${t('v2.reserva.elegir.siguientePaso')}`

  const field = (label: string, day: string | null, active: boolean) => (
    <div className="reserva__datefield">
      <span className="reserva__datefield-label">{label}</span>
      <div className={active ? 'reserva__datefield-box reserva__datefield-box--active tnum' : 'reserva__datefield-box tnum'} aria-live="polite">
        {day ? weekdayDayMonth(noon(day), lang) : t('v2.reserva.elegir.fechas.sinElegir')}
      </div>
    </div>
  )
  return (
    <Step
      title={t('v2.reserva.elegir.fechas.titulo')}
      intro={t('v2.reserva.elegir.fechas.lead')}
      primaryLabel={t('v2.reserva.elegir.ver')}
      disabled={!range.from || !range.to || unchanged}
      onPrimary={() => range.from && range.to && onNext({ type: type === 'nights' ? 'nights' : 'dates', checkIn: range.from, checkOut: range.to })}
      hint={<p className="reserva__hint">{hint}</p>}
    >
      <div className="reserva__datefields">
        {field(t('v2.reserva.datos.entrada'), range.from, !choosingCheckout)}
        {field(t('v2.reserva.datos.salida'), range.to, choosingCheckout)}
      </div>
      <RangeCalendar month={month} onMonthChange={setMonth} value={range} onChange={setRange} current={current} busyDates={busy} minDate={today} locale={lang} className="reserva__calendar" />
    </Step>
  )
}

function NightsStep({ stay, onNext }: StepProps) {
  const { t, lang } = useLang()
  const checkIn = dayOf(stay.dates.checkIn)
  const [nights, setNights] = useState(stay.dates.nights)
  const checkOut = addDays(checkIn, nights)
  const unchanged = nights === stay.dates.nights
  return (
    <Step
      title={t('v2.reserva.elegir.noches.titulo')}
      intro={t('v2.reserva.elegir.noches.lead', { date: weekdayDayMonth(stay.dates.checkIn, lang) })}
      primaryLabel={t('v2.reserva.elegir.ver')}
      disabled={unchanged}
      onPrimary={() => onNext({ type: 'nights', checkIn, checkOut })}
      hint={<p className="reserva__hint">{unchanged ? t('v2.reserva.elegir.noches.iguales') : t('v2.reserva.elegir.siguientePaso')}</p>}
    >
      <Stepper label={t('v2.reserva.elegir.noches.etiqueta')} value={nights} valueText={t('v2.reserva.elegir.noches.valor', { count: nights })} onChange={setNights} min={1} max={30} decLabel={t('v2.reserva.elegir.noches.quitar')} incLabel={t('v2.reserva.elegir.noches.anadir')} />
      <p className="reserva__summary tnum" aria-live="polite">
        {t('v2.reserva.elegir.noches.salida', { date: `${weekdayDayMonth(noon(checkOut), lang)} · ${time(stay.dates.checkOut, lang)}` })}
      </p>
    </Step>
  )
}

function GuestsStep({ stay, onNext }: StepProps) {
  const { t } = useLang()
  const apiLang = useApiLang()
  const [options] = useAsync(`reserva-cambios:${stay.guestCode}:personas`, (signal) => reservaService.getChangeOptions(stay, apiLang, signal))
  const max = asyncValue(options)?.options.find((o) => o.type === 'guests')?.maxGuests ?? stay.guests.adults + stay.guests.children
  const [adults, setAdults] = useState(stay.guests.adults)
  const [children, setChildren] = useState(stay.guests.children)
  const unchanged = adults === stay.guests.adults && children === stay.guests.children
  return (
    <Step
      title={t('v2.reserva.elegir.personas.titulo')}
      intro={t('v2.reserva.elegir.personas.lead', { count: max })}
      primaryLabel={t('v2.reserva.elegir.ver')}
      disabled={unchanged || options.kind === 'loading'}
      onPrimary={() => onNext({ type: 'guests', adults, children })}
      hint={<p className="reserva__hint">{unchanged ? t('v2.reserva.elegir.personas.iguales') : t('v2.reserva.elegir.siguientePaso')}</p>}
    >
      {options.kind === 'loading' ? (
        <div aria-busy="true">
          <Skeleton height={128} />
        </div>
      ) : (
        <div className="reserva__steppers">
          <Stepper label={t('v2.reserva.elegir.personas.adultos')} value={adults} valueText={String(adults)} onChange={setAdults} min={1} max={max - children} decLabel={t('v2.reserva.elegir.personas.quitarAdulto')} incLabel={t('v2.reserva.elegir.personas.anadirAdulto')} />
          <Stepper label={t('v2.reserva.elegir.personas.ninos')} value={children} valueText={String(children)} onChange={setChildren} min={0} max={max - adults} decLabel={t('v2.reserva.elegir.personas.quitarNino')} incLabel={t('v2.reserva.elegir.personas.anadirNino')} />
        </div>
      )}
      <p className="pagos__note">{t('v2.reserva.elegir.personas.registro')}</p>
    </Step>
  )
}

/** Sin endpoint (API actual, D36): no se puede elegir; la pantalla "No podemos hacer ese cambio" ofrece el contacto. */
function UnsupportedStep({ type }: { type: ChangeType }) {
  const { search } = useAppNavigate()
  return <Navigate to={withQuery(BOOKING_ROUTES.unavailable(type), search)} replace />
}

function UnitStep({ stay, onNext }: StepProps) {
  const { t, lang } = useLang()
  const [choices, reload] = useAsync(`unidades:${stay.guestCode}`, (signal) => reservaService.getUnitChoices(stay, signal))
  const [unitId, setUnitId] = useState<string | null>(null)
  if (choices.kind === 'unsupported') return <UnsupportedStep type="unit" />
  const list = asyncValue(choices) ?? []
  return (
    <Step title={t('v2.reserva.elegir.unidad.titulo')} intro={t('v2.reserva.elegir.unidad.lead')} primaryLabel={t('v2.reserva.elegir.ver')} disabled={!unitId} onPrimary={() => unitId && onNext({ type: 'unit', unitId })} hint={<p className="reserva__hint">{unitId ? t('v2.reserva.elegir.siguientePaso') : t('v2.reserva.elegir.eligeUna')}</p>}>
      {choices.kind === 'loading' ? (
        <div aria-busy="true">
          <Skeleton height={128} />
        </div>
      ) : choices.kind === 'error' ? (
        <SectionError code={choices.code} onRetry={reload} />
      ) : list.length === 0 ? (
        <EmptyState icon="home" title={t('v2.reserva.elegir.unidad.vacio')} headingLevel={2}>
          {t('v2.reserva.elegir.unidad.vacioTexto')}
        </EmptyState>
      ) : (
        <RadioCards
          label={t('v2.reserva.elegir.unidad.pregunta')}
          value={unitId}
          onChange={setUnitId}
          options={list.map((u) => ({ id: u.id, label: t('v2.reserva.elegir.unidad.opcion', { name: u.name, count: u.maxGuests, difference: u.difference.amount === 0 ? formatMoney(u.difference, lang) : signed(u.difference, lang) }) }))}
        />
      )}
    </Step>
  )
}

function TimeStep({ stay, type, onNext }: StepProps & { type: 'early_checkin' | 'late_checkout' }) {
  const { t, lang } = useLang()
  const [choices, reload] = useAsync(`horas:${stay.guestCode}:${type}`, (signal) => reservaService.getTimeChoices(stay, type, signal))
  const [value, setValue] = useState<string | null>(null)
  if (choices.kind === 'unsupported') return <UnsupportedStep type={type} />
  const early = type === 'early_checkin'
  const key = early ? 'entrada' : 'salida'
  const list = asyncValue(choices) ?? []
  return (
    <Step
      title={t(`v2.reserva.elegir.${key}.titulo`)}
      intro={t(`v2.reserva.elegir.${key}.lead`, { time: time(early ? stay.dates.checkIn : stay.dates.checkOut, lang) })}
      primaryLabel={t('v2.reserva.elegir.ver')}
      disabled={!value}
      onPrimary={() => value && onNext({ type, time: value })}
      hint={<p className="reserva__hint">{value ? t('v2.reserva.elegir.siguientePaso') : t('v2.reserva.elegir.eligeUna')}</p>}
    >
      {choices.kind === 'loading' ? (
        <div aria-busy="true">
          <Skeleton height={128} />
        </div>
      ) : choices.kind === 'error' ? (
        <SectionError code={choices.code} onRetry={reload} />
      ) : list.length === 0 ? (
        <EmptyState icon="clock" title={t(`v2.reserva.elegir.${key}.vacio`)} headingLevel={2} />
      ) : (
        <RadioCards label={t(`v2.reserva.elegir.${key}.pregunta`)} value={value} onChange={setValue} options={list.map((c) => ({ id: c.time, label: t(`v2.reserva.elegir.${key}.opcion`, { time: c.time, price: formatMoneyShort(c.price, lang) }) }))} />
      )}
    </Step>
  )
}

/** 8.5 · Paso 1 de un cambio: elegir (Reserva.dc.html screen=pick, §5b; el resto de tipos con el mismo patrón). */
export function CambiarElegir() {
  const { tipo = '' } = useParams()
  const type = TYPE_BY_SLUG[tipo]
  const stay = useBookingStay()
  const { go, search } = useAppNavigate()
  if (!type) return <Navigate to={withQuery(BOOKING_ROUTES.detail, search)} replace />
  const onNext = (input: ChangeInput) => {
    writeDraft({ guestCode: stay.guestCode, type, input, quote: null, reason: null })
    go(BOOKING_ROUTES.price(type))
  }
  const props = { stay, type, onNext }
  switch (type) {
    case 'dates':
      return <DatesStep {...props} />
    case 'nights':
      return <NightsStep {...props} />
    case 'guests':
      return <GuestsStep {...props} />
    case 'unit':
      return <UnitStep {...props} />
    case 'early_checkin':
    case 'late_checkout':
      return <TimeStep {...props} type={type} />
  }
}
